'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';

// ============================================================
// TYPES
// ============================================================
interface NavItem {
  label: string;
  href: string;
}

interface ChaletFeature {
  icon: string;
  title: string;
  description: string;
}

interface Experience {
  title: string;
  description: string;
  image: string;
  alt: string;
}

interface Activity {
  icon: string;
  title: string;
  description: string;
  image: string;
  alt: string;
}

interface LocationPoint {
  icon: string;
  title: string;
  description: string;
}

interface StoryPanel {
  label: string;
  title: string;
  description: string;
}

// ============================================================
// DATA
// ============================================================
const navItems: NavItem[] = [
{ label: 'Chalets', href: '#chalets' },
{ label: 'Experiences', href: '#experiences' },
{ label: 'Activities', href: '#activities' },
{ label: 'Location', href: '#location' },
{ label: 'Our Story', href: '#story' },
{ label: 'Contact', href: '#contact' }];


const chaletFeatures: ChaletFeature[] = [
{
  icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  title: 'Private Panoramic Terraces',
  description: 'Overlooking our desert waterhole, each terrace frames an uninterrupted vista of the ancient Namib landscape.'
},
{
  icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  title: 'Climate Controlled Comfort',
  description: 'Full air conditioning powered by sustainable energy ensures perfect comfort in every season.'
},
{
  icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
  title: 'Modern Amenities',
  description: 'Complimentary Wi-Fi, private digital safe, and a custom minibar — every detail considered for your comfort.'
},
{
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  title: 'Premium Refreshments',
  description: 'A fully stocked coffee and tea station awaits, ready to accompany your morning desert views.'
},
{
  icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  title: '24-Hour Clean Energy',
  description: '100% powered by our advanced, state-of-the-art solar system — luxury without compromise.'
}];


const experiences: Experience[] = [
{
  title: 'The Only À La Carte Dining in the Desert',
  description:
  'Indulge in a culinary journey at our premier restaurant, the only fine-dining à la carte establishment in the region. Savour exquisite, locally inspired cuisine while watching the desert change colour.',
  image: '/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1177-1784455826483.jpg',
  alt: 'Elegant desert dining table set with candles and fine tableware overlooking Namib desert at dusk'
},
{
  title: 'The Infinity Pool',
  description:
  'Cool off in our swimming pool, designed to offer an uninterrupted, infinite view across the glowing desert plains directly onto the majestic Rotstock Mountains.',
  image: '/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1385-1784455854677.jpg',
  alt: 'Infinity pool with panoramic view over golden desert plains and mountain range at sunset'
},
{
  title: 'From Golden Hour to the Cosmos at Our Boma',
  description:
  'As the afternoon fades, gather at our west-facing Boma. Savour a glass of premium wine while watching a magical sunset cast fiery cascades of colour over the Desert Plains. After dinner, witness a crystal-clear view of the Milky Way.',
  image: '/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1114-1784456287401.jpg',
  alt: 'Outdoor boma fire pit under a brilliant starry night sky in the Namib Desert'
}];


const activities: Activity[] = [
{
  icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707',
  title: 'The Living Desert',
  description:
  'Witness an unforgettable cascade of shifting colours across the landscapes at every sunrise and sunset.',
  image: "/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1064-1784455750205.jpg",
  alt: 'Dramatic Namib Desert sunrise with golden and red hues painting the sand dunes and rocky plains'
},
{
  icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  title: 'Vast Hiking Network',
  description:
  'Explore the terrain via 10 distinct, well-marked hiking trails ranging from leisurely walks to challenging treks.',
  image: "/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1281-1784455784493.jpg",
  alt: 'Hiker on a marked trail through dramatic Namibian desert canyon landscape with red rock formations'
},
{
  icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  title: 'Ancient Rock Art Excursions',
  description:
  'Embark on a rugged 4x4 off-road drive to view historic rock paintings estimated to be over 1,000 years old.',
  image: "/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1286-1784456458500.jpg",
  alt: 'Ancient San Bushman rock art paintings on desert rock face in Namibia estimated over 1000 years old'
},
{
  icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064',
  title: 'Exclusive Scenic Drives',
  description:
  'Traverse our pristine property to spot iconic desert wildlife — Oryx, Springbok, Ostriches, and rare Hartmann\'s Mountain Zebras.',
  image: "/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1255-1784455875698.jpg",
  alt: 'Oryx antelope standing in golden Namib Desert landscape with mountains in the background'
}];


const locationPoints: LocationPoint[] = [
{
  icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  title: 'Private Air Travel',
  description: 'Arrive on our own well-maintained, private landing strip. Perfect for private pilots and luxury fly-in Safari Operators.'
},
{
  icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  title: 'Sossusvlei Gateway',
  description: 'Located just 140km from the world-famous dunes of Sossusvlei — the iconic red sand dunes of the Namib.'
},
{
  icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
  title: 'Coastal Connection',
  description: 'Within 3 hours you reach the coastal towns of Walvis Bay and Swakopmund by driving along the C14.'
},
{
  icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  title: 'Capital Proximity',
  description: '220km from Windhoek via the scenic C26 Gamsberg pass — a breathtaking mountain drive through ancient geology.'
},
{
  icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  title: 'Our Location',
  description: 'Situated 45km north of Solitaire, just 7km off the C14 gravel road toward Walvis Bay.'
}];


const storyPanels: StoryPanel[] = [
{
  label: 'The Name',
  title: 'From Rotstock to Rostock',
  description:
  'Originally named "Rotstock" after the striking red gneiss mountains bordering to our north, the Farm lost the letter "t" during the 1960s farm division to become known as Farm Rostock as we know it today.'
},
{
  label: 'The Climate',
  title: 'A Celebration of Life',
  description:
  'Nestled on the edge of the desert, we receive a mere 100mm to 150mm of rain annually, making every drop a celebration of life in one of Earth\'s most ancient landscapes.'
},
{
  label: 'The Landscape',
  title: 'Rivers of the Desert',
  description:
  'We are bordered by the ephemeral Gaub River, which cuts through the desert canyons before meeting the legendary Kuiseb River in the Namib-Naukluft Park.'
},
{
  label: 'The Lodge',
  title: 'A New Era of Desert Luxury',
  description:
  'Built in 1998 by hotelier Wolfgang "Kücki" Kühhirt and designed by Piet Odendaal. A new era began in 2025 when the lodge was acquired by a new owner. Robert launched a comprehensive interior revamp in January 2026. Today the timeless "cave-like" igloo structure meets a sleek elevated standard of modern desert luxury.'
}];


// ============================================================
// HOOK: useScrollAnimation
// ============================================================
function useScrollAnimation() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('.fade-in, .fade-in-left, .fade-in-right');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);
}

// ============================================================
// COMPONENT: Navigation
// ============================================================
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = useCallback((href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-4 transition-all duration-600 ${
        scrolled ?
        'bg-lodge-offwhite shadow-sm' :
        'bg-transparent'}`
        }>
        
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex-shrink-0"
          aria-label="Scroll to top">
          
          <Image
            src="/assets/images/RostockRitz_Logo_202604__2colour-1783437072268.png"
            alt="Rostock Ritz Desert Lodge logo with dual RR monogram in brown and gold"
            width={120}
            height={60}
            className="object-contain"
            priority />
          
        </button>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) =>
          <button
            key={item.label}
            onClick={() => handleNavClick(item.href)}
            className={`font-body text-xs tracking-widest uppercase transition-colors duration-300 ${
            scrolled ? 'text-lodge-brown hover:text-lodge-gold' : 'text-white hover:text-lodge-gold'}`
            }>
            
              {item.label}
            </button>
          )}
        </div>

        {/* CTA + Hamburger */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleNavClick('#contact')}
            className={`hidden lg:inline-block text-xs tracking-widest uppercase font-body font-light px-6 py-3 border transition-all duration-400 ${
            scrolled ?
            'border-lodge-gold text-lodge-gold hover:bg-lodge-gold hover:text-white' : 'border-white text-white hover:bg-white hover:text-lodge-brown'}`
            }>
            
            Make a Reservation
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden flex flex-col gap-1.5 p-2 ${
            scrolled ? 'text-lodge-brown' : 'text-white'}`
            }
            aria-label="Toggle menu">
            
            <span className={`block w-6 h-px transition-all duration-300 ${
            scrolled ? 'bg-lodge-brown' : 'bg-white'} ${
            menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-px transition-all duration-300 ${
            scrolled ? 'bg-lodge-brown' : 'bg-white'} ${
            menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-px transition-all duration-300 ${
            scrolled ? 'bg-lodge-brown' : 'bg-white'} ${
            menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-lodge-dark flex flex-col items-center justify-center transition-all duration-600 ${
        menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`
        }>
        
        <div className="flex flex-col items-center gap-8">
          {navItems.map((item) =>
          <button
            key={item.label}
            onClick={() => handleNavClick(item.href)}
            className="font-heading text-2xl text-lodge-offwhite uppercase tracking-widest hover:text-lodge-gold transition-colors duration-300">
            
              {item.label}
            </button>
          )}
          <button
            onClick={() => handleNavClick('#contact')}
            className="mt-4 btn-outline-gold">
            
            Make a Reservation
          </button>
        </div>
      </div>
    </>);

}

// ============================================================
// COMPONENT: HeroSection
// ============================================================
function HeroSection() {
  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-end pb-20 overflow-hidden">
      
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1125-1784456367701.jpg"
          alt="Vast Namib Desert landscape at golden hour with dramatic red sand dunes and endless horizon"
          fill
          className="object-cover object-center"
          priority />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-lodge-dark via-lodge-dark/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-lodge-dark/30 to-transparent" />
      </div>

      {/* Large background text - Ongava style */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <h1
          className="font-heading font-light text-white/5 uppercase leading-none select-none"
          style={{ fontSize: 'clamp(80px, 18vw, 280px)', letterSpacing: '0.05em' }}>
          
          ROSTOCK
        </h1>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-6">
            Namib Desert · Namibia
          </p>
          <h2
            className="font-heading font-light text-white uppercase leading-tight mb-8"
            style={{ fontSize: 'clamp(28px, 4vw, 56px)', letterSpacing: '0.12em' }}>
            
            Escape to Infinite Silence at Rostock Ritz Desert Lodge
          </h2>
          <div className="w-16 h-px bg-lodge-gold mb-8" />
          <p className="font-body text-white/80 text-sm md:text-base font-light leading-relaxed tracking-wide max-w-2xl mb-10">
            Experience Namibia&apos;s desert landscape from the private comfort of our newly reimagined luxury chalets.
            High above the plains, where endless horizons meet absolute silence, Rostock Ritz Desert Lodge invites
            high-end travellers to slow down, disconnect, and immerse themselves in the deep tranquillity of the Namib Desert.
          </p>
          <button
            onClick={() => document.querySelector('#chalets')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-outline-white">
            
            Discover More
          </button>
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: ChaletsSection
// ============================================================
function ChaletsSection() {
  return (
    <section id="chalets" className="py-24 lg:py-40 bg-sand-DEFAULT">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Accommodation</p>
          <h2 className="section-heading text-lodge-brown mb-6">
            The New Standard of Desert Luxury
          </h2>
          <div className="gold-divider" />
          <p className="body-text text-lodge-grey max-w-2xl mx-auto mt-6">
            Following a comprehensive reimagining, our exclusive lodge now features just{' '}
            <span className="text-lodge-brown font-medium">11 spacious, premium luxury chalets</span>{' '}
            designed for the discerning traveller. Each chalet has doubled in size and offers a seamless
            blend of wild nature and modern sophistication.
          </p>
        </div>

        {/* Split layout: Image + Features */}
        <div className="grid lg:grid-cols-2 gap-0 items-stretch mb-20">
          {/* Image */}
          <div className="relative h-96 lg:h-auto min-h-96 overflow-hidden fade-in-left">
            <Image
              src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1118-1784456857296.jpg"
              alt="Luxury desert chalet interior with panoramic terrace overlooking Namib Desert waterhole at sunset"
              fill
              className="object-cover transition-transform duration-800 hover:scale-105" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-lodge-dark/40 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <p className="font-body text-white text-xs tracking-widest uppercase">11 Luxury Chalets</p>
            </div>
          </div>

          {/* Features */}
          <div className="bg-lodge-dark p-10 lg:p-14 fade-in-right">
            <div className="space-y-8">
              {chaletFeatures.map((feature, idx) =>
              <div key={idx} className="flex gap-5 group">
                  <div className="flex-shrink-0 w-10 h-10 border border-lodge-gold/40 flex items-center justify-center group-hover:border-lodge-gold transition-colors duration-300">
                    <svg className="w-5 h-5 text-lodge-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={feature.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-1">
                      {feature.title}
                    </h3>
                    <p className="font-body text-lodge-offwhite/70 text-sm font-light leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second image row */}
        <div className="grid md:grid-cols-3 gap-4 fade-in">
          <div className="relative h-64 overflow-hidden group">
            <Image
              src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1120-1784456112287.jpg"
              alt="Spacious luxury chalet bedroom with desert view through large windows and natural stone walls"
              fill
              className="object-cover transition-transform duration-800 group-hover:scale-105" />
            
            <div className="absolute inset-0 bg-lodge-dark/20 group-hover:bg-lodge-dark/10 transition-colors duration-300" />
          </div>
          <div className="relative h-64 overflow-hidden group">
            <Image
              src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1426-1784456160334.jpg"
              alt="Private terrace of desert chalet with lounge chairs overlooking waterhole and desert plains"
              fill
              className="object-cover transition-transform duration-800 group-hover:scale-105" />
            
            <div className="absolute inset-0 bg-lodge-dark/20 group-hover:bg-lodge-dark/10 transition-colors duration-300" />
          </div>
          <div className="relative h-64 overflow-hidden group">
            <Image
              src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1441-1784456202379.jpg"
              alt="Modern luxury bathroom in desert lodge with natural stone finishes and ambient lighting"
              fill
              className="object-cover transition-transform duration-800 group-hover:scale-105" />
            
            <div className="absolute inset-0 bg-lodge-dark/20 group-hover:bg-lodge-dark/10 transition-colors duration-300" />
          </div>
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: ExperiencesSection
// ============================================================
function ExperiencesSection() {
  return (
    <section id="experiences" className="bg-lodge-dark py-24 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Dining & Leisure</p>
          <h2 className="section-heading text-lodge-offwhite mb-6">
            Unrivalled Desert Experiences
          </h2>
          <div className="gold-divider" />
        </div>

        {/* Experience Panels */}
        <div className="space-y-0">
          {experiences.map((exp, idx) =>
          <div
            key={idx}
            className={`grid lg:grid-cols-2 gap-0 items-stretch ${
            idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`
            }>
            
              {/* Image */}
              <div
              className={`relative h-80 lg:h-96 overflow-hidden ${
              idx % 2 === 1 ? 'lg:order-2' : ''} ${
              idx % 2 === 0 ? 'fade-in-left' : 'fade-in-right'}`}>
              
                <Image
                src={exp.image}
                alt={exp.alt}
                fill
                className="object-cover transition-transform duration-800 hover:scale-105" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-lodge-dark/60 to-transparent" />
              </div>

              {/* Text */}
              <div
              className={`flex flex-col justify-center p-10 lg:p-16 border border-lodge-gold/10 ${
              idx % 2 === 1 ? 'lg:order-1' : ''} ${
              idx % 2 === 0 ? 'fade-in-right' : 'fade-in-left'}`}>
              
                <span className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">
                  0{idx + 1}
                </span>
                <h3
                className="font-heading text-lodge-offwhite font-light uppercase mb-4"
                style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', letterSpacing: '0.12em' }}>
                
                  {exp.title}
                </h3>
                <div className="w-10 h-px bg-lodge-gold mb-6" />
                <p className="font-body text-lodge-offwhite/70 text-sm font-light leading-relaxed">
                  {exp.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: ActivitiesSection
// ============================================================
function ActivitiesSection() {
  return (
    <section id="activities" className="py-24 lg:py-40 bg-sand-DEFAULT">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Explore</p>
          <h2 className="section-heading text-lodge-brown mb-6">Awaken Your Senses</h2>
          <div className="gold-divider" />
        </div>

        {/* 2x2 Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {activities.map((activity, idx) =>
          <div
            key={idx}
            className={`group relative overflow-hidden ${
            idx % 2 === 0 ? 'fade-in-left' : 'fade-in-right'}`
            }>
            
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <Image
                src={activity.image}
                alt={activity.alt}
                fill
                className="object-cover transition-transform duration-800 group-hover:scale-105" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-lodge-dark via-lodge-dark/40 to-transparent" />
              </div>

              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 border border-lodge-gold/60 flex items-center justify-center">
                    <svg className="w-4 h-4 text-lodge-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={activity.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">
                      {activity.title}
                    </h3>
                    <p className="font-body text-white/80 text-sm font-light leading-relaxed">
                      {activity.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: LocationSection
// ============================================================
function LocationSection() {
  return (
    <section id="location" className="bg-lodge-dark py-24 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Getting Here</p>
          <h2 className="section-heading text-lodge-offwhite mb-6">
            Perfectly Positioned in the Namib
          </h2>
          <div className="gold-divider" />
          <p className="body-text text-lodge-offwhite/70 max-w-2xl mx-auto mt-6">
            Nestled in the heart of the Namib Desert wilderness, Rostock Ritz is the ultimate base for your
            multi-day Namibian itinerary, offering effortless connectivity to the country&apos;s most iconic destinations.
          </p>
        </div>

        {/* Split: Map + Points */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Map Placeholder */}
          <div className="fade-in-left">
            <div className="relative h-96 lg:h-[500px] overflow-hidden border border-lodge-gold/20">
              <Image
                src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1215-1784455953950.jpg"
                alt="Aerial view of Namib Desert landscape showing the location of Rostock Ritz Desert Lodge near Solitaire"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0 bg-lodge-dark/30" />
              {/* Coordinate overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-lodge-dark/80 border border-lodge-gold/30 p-4">
                  <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Lodge Coordinates</p>
                  <p className="font-body text-lodge-offwhite text-sm">S 23° 33.568&apos; · E 015° 50.570&apos;</p>
                  <p className="font-body text-lodge-offwhite/60 text-xs mt-1">45km north of Solitaire · 7km off C14</p>
                </div>
              </div>
            </div>

            {/* Embedded map */}
            <div className="mt-4 border border-lodge-gold/20 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3!2d15.8438!3d-23.5594!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1c2a4b5c6d7e8f9a%3A0x1234567890abcdef!2sRostock%20Ritz%20Desert%20Lodge!5e0!3m2!1sen!2sna!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Rostock Ritz Desert Lodge location map" />
              
            </div>
          </div>

          {/* Location Points */}
          <div className="space-y-6 fade-in-right">
            {locationPoints.map((point, idx) =>
            <div
              key={idx}
              className="flex gap-5 p-6 border border-lodge-gold/10 hover:border-lodge-gold/30 transition-colors duration-300 group">
              
                <div className="flex-shrink-0 w-10 h-10 border border-lodge-gold/40 flex items-center justify-center group-hover:border-lodge-gold transition-colors duration-300">
                  <svg className="w-5 h-5 text-lodge-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={point.icon} />
                  </svg>
                </div>
                <div>
                  <h3 className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-1">
                    {point.title}
                  </h3>
                  <p className="font-body text-lodge-offwhite/70 text-sm font-light leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: StorySection
// ============================================================
function StorySection() {
  return (
    <section id="story" className="py-24 lg:py-40 bg-sand-DEFAULT">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Heritage</p>
          <h2 className="section-heading text-lodge-brown mb-6">
            From Rotstock to Rostock: A Desert Legacy
          </h2>
          <div className="gold-divider" />
        </div>

        {/* Full-width image */}
        <div className="relative h-72 md:h-96 overflow-hidden mb-20 fade-in">
          <Image
            src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1123-1784456258520.jpg"
            alt="Panoramic view of the ancient red gneiss Rotstock mountains bordering Rostock Ritz Desert Lodge in Namibia"
            fill
            className="object-cover" />
          
          <div className="absolute inset-0 bg-gradient-to-r from-lodge-dark/60 via-lodge-dark/20 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-10 lg:px-16 max-w-lg">
              <p className="font-heading text-white/90 font-light italic text-xl md:text-2xl leading-relaxed" style={{ letterSpacing: '0.05em' }}>
                &ldquo;Where raw Namibian history meets sophisticated comfort.&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* Story Grid */}
        <div className="grid md:grid-cols-2 gap-0">
          {storyPanels.map((panel, idx) =>
          <div
            key={idx}
            className={`p-10 lg:p-14 border border-lodge-brown/10 ${
            idx % 2 === 0 ? 'fade-in-left' : 'fade-in-right'} ${

            idx < 2 ? 'border-b' : ''} ${

            idx % 2 === 0 ? 'border-r' : ''} ${

            idx % 4 < 2 ? 'bg-sand-DEFAULT' : 'bg-lodge-brown'}`
            }>
            
              <span
              className={`font-body text-xs tracking-ultra-wide uppercase mb-3 block ${
              idx % 4 < 2 ? 'text-lodge-gold' : 'text-lodge-gold'}`
              }>
              
                {panel.label}
              </span>
              <h3
              className={`font-heading font-light uppercase mb-4 ${
              idx % 4 < 2 ? 'text-lodge-brown' : 'text-lodge-offwhite'}`
              }
              style={{ fontSize: 'clamp(18px, 2vw, 26px)', letterSpacing: '0.15em' }}>
              
                {panel.title}
              </h3>
              <div className={`w-10 h-px mb-5 ${
            idx % 4 < 2 ? 'bg-lodge-gold' : 'bg-lodge-gold/60'}`
            } />
              <p
              className={`font-body text-sm font-light leading-relaxed ${
              idx % 4 < 2 ? 'text-lodge-grey' : 'text-lodge-offwhite/80'}`
              }>
              
                {panel.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: ContactSection
// ============================================================
function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkin: '',
    checkout: '',
    guests: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="bg-lodge-grey py-24 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20 fade-in">
          <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-4">Reservations</p>
          <h2 className="section-heading text-lodge-offwhite mb-6">Contact Us</h2>
          <div className="gold-divider" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Details */}
          <div className="fade-in-left">
            <div className="mb-10">
              <Image
                src="/assets/images/RostockRitz_Logo_202604__2colour-1783437072268.png"
                alt="Rostock Ritz Desert Lodge logo"
                width={140}
                height={70}
                className="object-contain brightness-0 invert opacity-80 mb-8" />
              
              <p className="font-body text-lodge-offwhite/70 text-sm font-light leading-relaxed mb-8">
                We would love to welcome you to Rostock Ritz Desert Lodge. For reservations and enquiries,
                please reach out to our team directly.
              </p>
            </div>

            <div className="space-y-6">
              <div className="border-t border-lodge-gold/20 pt-6">
                <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Lodge Direct</p>
                <a
                  href="https://wa.me/26481884912"
                  className="font-body text-lodge-offwhite text-sm hover:text-lodge-gold transition-colors duration-300">
                  
                  +264 (0)81 884 9122
                </a>
                <p className="font-body text-lodge-offwhite/50 text-xs mt-1">WhatsApp Only</p>
              </div>

              <div className="border-t border-lodge-gold/20 pt-6">
                <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Reservations</p>
                <a
                  href="tel:+26481258572"
                  className="font-body text-lodge-offwhite text-sm hover:text-lodge-gold transition-colors duration-300">
                  
                  +264 (0)81 258 5722
                </a>
              </div>

              <div className="border-t border-lodge-gold/20 pt-6">
                <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Email</p>
                <a
                  href="mailto:reservations@rostock-ritz.com"
                  className="font-body text-lodge-offwhite text-sm hover:text-lodge-gold transition-colors duration-300">
                  
                  reservations@rostock-ritz.com
                </a>
              </div>

              <div className="border-t border-lodge-gold/20 pt-6">
                <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Lodge Coordinates</p>
                <p className="font-body text-lodge-offwhite/80 text-sm">S 23° 33.568&apos; · E 015° 50.570&apos;</p>
              </div>

              <div className="border-t border-lodge-gold/20 pt-6">
                <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-2">Airstrip Coordinates</p>
                <p className="font-body text-lodge-offwhite/80 text-sm">S 23° 31&apos; 53.2&quot; · E 15° 48&apos; 37.8&quot;</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="fade-in-right">
            {submitted ?
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
                <div className="w-16 h-16 border border-lodge-gold flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-lodge-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-heading text-lodge-offwhite text-2xl uppercase tracking-widest mb-4">
                  Enquiry Sent
                </h3>
                <p className="font-body text-lodge-offwhite/70 text-sm">
                  Thank you for your enquiry. Our reservations team will be in touch shortly.
                </p>
              </div> :

            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                      Full Name
                    </label>
                    <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300 placeholder-lodge-offwhite/30"
                    placeholder="Your full name" />
                  
                  </div>
                  <div>
                    <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                      Email Address
                    </label>
                    <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300 placeholder-lodge-offwhite/30"
                    placeholder="your@email.com" />
                  
                  </div>
                </div>

                <div>
                  <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                    Phone Number
                  </label>
                  <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300 placeholder-lodge-offwhite/30"
                  placeholder="+1 234 567 8900" />
                
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                      Check-in Date
                    </label>
                    <input
                    type="date"
                    name="checkin"
                    value={formData.checkin}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300" />
                  
                  </div>
                  <div>
                    <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                      Check-out Date
                    </label>
                    <input
                    type="date"
                    name="checkout"
                    value={formData.checkout}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300" />
                  
                  </div>
                </div>

                <div>
                  <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                    Number of Guests
                  </label>
                  <select
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                  className="w-full bg-lodge-grey border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300">
                  
                    <option value="">Select guests</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) =>
                  <option key={n} value={n}>
                        {n} {n === 1 ? 'Guest' : 'Guests'}
                      </option>
                  )}
                  </select>
                </div>

                <div>
                  <label className="font-body text-lodge-gold text-xs tracking-widest uppercase block mb-2">
                    Message / Special Requests
                  </label>
                  <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-transparent border border-lodge-gold/30 text-lodge-offwhite font-body text-sm px-4 py-3 focus:outline-none focus:border-lodge-gold transition-colors duration-300 placeholder-lodge-offwhite/30 resize-none"
                  placeholder="Tell us about your ideal stay..." />
                
                </div>

                <button
                type="submit"
                className="w-full btn-outline-gold py-4 text-center">
                
                  Send Enquiry
                </button>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: CTABanner
// ============================================================
function CTABanner() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <Image
          src="/assets/images/RostockRitz-snowballstudio-15-16Juli2026-1070-1784456072672.jpg"
          alt="Oryx silhouetted against a fiery Namib Desert sunset with golden sky and desert plains"
          fill
          className="object-cover" />
        
        <div className="absolute inset-0 bg-lodge-dark/70" />
      </div>
      <div className="relative z-10 text-center max-w-3xl mx-auto px-6 fade-in">
        <p className="font-body text-lodge-gold text-xs tracking-ultra-wide uppercase mb-6">Begin Your Journey</p>
        <h2
          className="font-heading font-light text-white uppercase mb-6"
          style={{ fontSize: 'clamp(24px, 3.5vw, 48px)', letterSpacing: '0.15em' }}>
          
          Ready to Book the Trip of a Lifetime
        </h2>
        <div className="gold-divider" />
        <p className="font-body text-white/70 text-sm font-light leading-relaxed mb-10 max-w-xl mx-auto">
          There&apos;s no place on earth like the Namib, and no place in the Namib like Rostock Ritz.
          Experience its soul-stirring wonders for yourself. Book your stay today.
        </p>
        <a href="mailto:reservations@rostock-ritz.com" className="btn-outline-white">
          Make a Reservation
        </a>
      </div>
    </section>);

}

// ============================================================
// COMPONENT: Footer
// ============================================================
function Footer() {
  return (
    <footer className="bg-lodge-dark border-t border-lodge-gold/20">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          {/* Logo + tagline */}
          <div className="text-center md:text-left">
            <Image
              src="/assets/images/RostockRitz_Logo_202604__2colour-1783437072268.png"
              alt="Rostock Ritz Desert Lodge logo"
              width={130}
              height={65}
              className="object-contain mx-auto md:mx-0 mb-6 brightness-0 invert opacity-70" />
            
            <p className="font-body text-lodge-offwhite/50 text-xs font-light leading-relaxed tracking-wide">
              Luxury desert lodge in the heart of the Namib.<br />
              45km north of Solitaire, Namibia.
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center">
            <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-6">Quick Links</p>
            <ul className="space-y-3">
              {[
              { label: 'Chalets', href: '#chalets' },
              { label: 'Experiences', href: '#experiences' },
              { label: 'Activities', href: '#activities' },
              { label: 'Location', href: '#location' },
              { label: 'Our Story', href: '#story' },
              { label: 'Contact', href: '#contact' }].
              map((link) =>
              <li key={link.label}>
                  <button
                  onClick={() => document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' })}
                  className="font-body text-lodge-offwhite/60 text-xs tracking-wide uppercase hover:text-lodge-gold transition-colors duration-300">
                  
                    {link.label}
                  </button>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center md:text-right">
            <p className="font-body text-lodge-gold text-xs tracking-widest uppercase mb-6">Contact</p>
            <div className="space-y-3">
              <p className="font-body text-lodge-offwhite/60 text-xs">
                <a href="https://wa.me/26481884912" className="hover:text-lodge-gold transition-colors duration-300">
                  +264 (0)81 884 9122
                </a>
                <span className="block text-lodge-offwhite/30">WhatsApp Only</span>
              </p>
              <p className="font-body text-lodge-offwhite/60 text-xs">
                <a href="mailto:reservations@rostock-ritz.com" className="hover:text-lodge-gold transition-colors duration-300">
                  reservations@rostock-ritz.com
                </a>
              </p>
            </div>

            {/* Social */}
            <div className="flex gap-4 mt-8 justify-center md:justify-end">
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rostock Ritz on Instagram"
                className="w-8 h-8 border border-lodge-gold/30 flex items-center justify-center text-lodge-offwhite/60 hover:border-lodge-gold hover:text-lodge-gold transition-all duration-300">
                
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rostock Ritz on Facebook"
                className="w-8 h-8 border border-lodge-gold/30 flex items-center justify-center text-lodge-offwhite/60 hover:border-lodge-gold hover:text-lodge-gold transition-all duration-300">
                
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-lodge-gold/10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-lodge-offwhite/40 text-xs tracking-wide">
            © 2025 Rostock Ritz Desert Lodge. All Rights Reserved.
          </p>
          <div className="flex gap-6">
            <button className="font-body text-lodge-offwhite/40 text-xs tracking-wide hover:text-lodge-gold transition-colors duration-300">
              Privacy Policy
            </button>
            <button className="font-body text-lodge-offwhite/40 text-xs tracking-wide hover:text-lodge-gold transition-colors duration-300">
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </div>
    </footer>);

}

// ============================================================
// MAIN PAGE
// ============================================================
export default function HomePage() {
  useScrollAnimation();

  return (
    <main className="overflow-x-hidden">
      <Navigation />
      <HeroSection />
      <ChaletsSection />
      <ExperiencesSection />
      <ActivitiesSection />
      <LocationSection />
      <StorySection />
      <CTABanner />
      <ContactSection />
      <Footer />
    </main>);

}